import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useApi } from './useApi';
import './JoinSociety.css';
import { FiUsers, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const JoinSociety = () => {
    const [searchParams] = useSearchParams();
    const inviteCode = searchParams.get('code');
    const { currentUser, userProfile, fetchUserProfile, setCurrentSociety } = useAuth();
    const { authenticatedFetch } = useApi();
    const navigate = useNavigate();
    
    const [societyInfo, setSocietyInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [claimCandidates, setClaimCandidates] = useState(null); // null = not in claim step
    const [claiming, setClaiming] = useState(false);
    const [claimSocietyId, setClaimSocietyId] = useState(null);

    useEffect(() => {
        if (!inviteCode) {
            setError('No invite code provided.');
            setLoading(false);
            return;
        }

        // If not logged in, save code and redirect
        if (!currentUser) {
            localStorage.setItem('pendingInviteCode', inviteCode);
            navigate('/login?redirect=join');
            return;
        }

        // If user is already a member of this society, skip straight to success
        if (userProfile?.societies?.find(s => s.invite_code === inviteCode.toUpperCase())) {
            localStorage.removeItem('pendingInviteCode');
            const existing = userProfile.societies.find(s => s.invite_code === inviteCode.toUpperCase());
            setCurrentSociety(existing);
            setJoined(true);
            setSocietyInfo({ name: existing.name, invite_code: existing.invite_code });
            setLoading(false);
            return;
        }

        const fetchInfo = async () => {
            try {
                const res = await authenticatedFetch(`/auth/society-info/${inviteCode}`);
                if (res.ok) {
                    setSocietyInfo(await res.json());
                } else {
                    setError('Invalid or expired invite code.');
                }
            } catch (err) {
                setError('Failed to load society information.');
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inviteCode, currentUser, navigate, authenticatedFetch]);

    const finalizeJoin = async () => {
        localStorage.removeItem('pendingInviteCode');
        const updatedProfile = await fetchUserProfile(currentUser);
        if (updatedProfile?.societies) {
            const target = updatedProfile.societies.find(s => s.invite_code === inviteCode.toUpperCase());
            if (target) setCurrentSociety(target);
        }
        setJoined(true);
    };

    const handleJoin = async () => {
        setJoining(true);
        setError('');
        try {
            const res = await authenticatedFetch('/auth/join-society', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ invite_code: inviteCode })
            });
            const data = await res.json();

            if (res.ok || data.error === 'You are already a member of this society') {
                // If the server found possible existing player records, let the
                // user confirm which one is them before we link (avoids both
                // duplicates and silently linking to the wrong person).
                if (data.status === 'needs_claim' && data.candidates?.length) {
                    setClaimSocietyId(data.society_id);
                    setClaimCandidates(data.candidates);
                } else {
                    await finalizeJoin();
                }
            } else {
                setError(data.error || 'Failed to join society.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setJoining(false);
        }
    };

    const handleClaim = async (playerId) => {
        setClaiming(true);
        setError('');
        try {
            const body = playerId
                ? { society_id: claimSocietyId, player_id: playerId }
                : { society_id: claimSocietyId, create_new: true };
            const res = await authenticatedFetch('/auth/claim-player', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (res.ok) {
                setClaimCandidates(null);
                await finalizeJoin();
            } else {
                setError(data.error || 'Could not link your record. Please try again.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setClaiming(false);
        }
    };

    if (loading) return <div className="join-loading">Checking invitation...</div>;

    return (
        <div className="join-container">
            <div className="join-card premium-card">
                <div className="join-icon">
                    {joined ? <FiCheckCircle size={48} color="#28a745" /> : <FiUsers size={48} color="#0077be" />}
                </div>

                {claimCandidates ? (
                    <div className="join-content">
                        <h2>Is this you?</h2>
                        <p className="invite-text">We found existing player record{claimCandidates.length > 1 ? 's' : ''} in <strong>{societyInfo?.name}</strong> that might be you. Linking keeps your handicap and past scores.</p>
                        <div className="claim-candidate-list">
                            {claimCandidates.map(c => (
                                <button
                                    key={c.player_id}
                                    className="claim-candidate-btn"
                                    onClick={() => handleClaim(c.player_id)}
                                    disabled={claiming}
                                >
                                    <span className="claim-candidate-name">{c.name}</span>
                                    <span className="claim-candidate-meta">
                                        {c.handicap != null ? `HCP ${c.handicap}` : 'No handicap'}
                                        {c.tier === 'possible' ? ' · possible match' : ''}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {error && <p className="join-inline-error">{error}</p>}
                        <button
                            className="btn-secondary claim-none-btn"
                            onClick={() => handleClaim(null)}
                            disabled={claiming}
                        >
                            {claiming ? 'Linking…' : "None of these — I'm new here"}
                        </button>
                    </div>
                ) : joined ? (
                    <div className="join-content">
                        <h2>You're In!</h2>
                        <p className="invite-text">Welcome to <strong>{societyInfo?.name}</strong></p>
                        <div className="join-actions">
                            <button className="btn-primary" onClick={() => navigate('/home')}>Get Started</button>
                        </div>
                    </div>
                ) : error ? (
                    <div className="join-error-view">
                        <FiAlertCircle size={32} color="#dc3545" />
                        <h2>Invitation Issue</h2>
                        <p>{error}</p>
                        <div className="join-actions">
                            {societyInfo && (
                                <button className="btn-primary" onClick={handleJoin} disabled={joining}>
                                    {joining ? 'Retrying...' : 'Try Again'}
                                </button>
                            )}
                            <button className="btn-secondary" onClick={() => navigate('/home')}>Go Home</button>
                        </div>
                    </div>
                ) : (
                    <div className="join-content">
                        <h2>Join Golf Society</h2>
                        <p className="invite-text">You've been invited to join:</p>
                        <div className="society-preview">
                            <span className="society-name">{societyInfo?.name}</span>
                            <span className="society-code">Code: {societyInfo?.invite_code}</span>
                        </div>

                        <p className="join-notice">Joining will link your user profile to this society and create your player record.</p>

                        <div className="join-actions">
                            <button className="btn-secondary" onClick={() => navigate('/home')}>Cancel</button>
                            <button
                                className="btn-primary join-btn"
                                onClick={handleJoin}
                                disabled={joining}
                            >
                                {joining ? 'Joining...' : 'Confirm & Join'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JoinSociety;
